import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  GridView as DashboardIcon,
  MonitorHeart as BatchMonitorIcon,
  AssignmentTurnedIn as WorkOrdersIcon,
  PlayCircleOutline as BatchSearchIcon,
  TaskAlt as QAIcon,
  FindInPage as AuditIcon,
  AccountTree as EditorIcon,
  Inventory as InventoryIcon,
  LocalShipping as ReceivingIcon,
  FactCheck as InspectionIcon,
  Warning as QuarantineIcon,
  Business as SuppliersIcon,
  ShoppingCart as POIcon,
} from '@mui/icons-material';

export const collapsedWidth = 50;
export const expandedWidth = 200;

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const menuItems = [
  { text: 'Overview', path: '/dashboard', icon: <DashboardIcon />, section: 'EBR' },
  { text: 'Batch Monitor', path: '/batch-monitor', icon: <BatchMonitorIcon /> },
  { text: 'Batch Execution', path: '/batch-search', icon: <BatchSearchIcon /> },
  { text: 'QA Review', path: '/qa-review', icon: <QAIcon /> },
  { text: 'Audit Trail', path: '/audit-trail', icon: <AuditIcon /> },
  { text: 'MBR Editor', path: '/mbr-editor', icon: <EditorIcon /> },
  { text: 'Work Orders', path: '/work-orders', icon: <WorkOrdersIcon />, section: 'WMC' },
  { text: 'Inventory', path: '/wmc/inventory', icon: <InventoryIcon /> },
  { text: 'Receiving', path: '/wmc/receiving', icon: <ReceivingIcon /> },
  { text: 'Inspections', path: '/wmc/inspections', icon: <InspectionIcon /> },
  { text: 'Quarantine', path: '/wmc/quarantine', icon: <QuarantineIcon /> },
  { text: 'Suppliers', path: '/wmc/suppliers', icon: <SuppliersIcon /> },
  { text: 'Purchase Orders', path: '/wmc/purchase-orders', icon: <POIcon /> },
];

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const drawerWidth = isMobile ? expandedWidth : (open ? expandedWidth : collapsedWidth);
  const showLabels = isMobile || open;

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const drawerContent = (
    <Box sx={{ overflow: 'auto' }}>
      <List sx={{ p: 0 }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
          {(item as any).section === 'WMC' && (
            <Divider sx={{ borderColor: '#374151', my: 0.5 }} />
          )}
          <ListItem disablePadding sx={{ display: 'block' }}>
            <Tooltip title={showLabels ? '' : item.text} placement="right" arrow>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                selected={location.pathname === item.path || (item.path === '/batch-search' && location.pathname.startsWith('/batch-execution'))}
                sx={{
                  justifyContent: showLabels ? 'initial' : 'center',
                  px: showLabels ? 1.5 : 0,
                  py: 1.5,
                  minHeight: 44,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    borderRight: '3px solid #3B82F6',
                    '& .MuiListItemIcon-root': { color: '#3B82F6' },
                    '& .MuiListItemText-primary': { color: '#3B82F6' },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(59, 130, 246, 0.05)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path ? '#3B82F6' : '#D1D5DB',
                    minWidth: 0,
                    mr: showLabels ? 1 : 0,
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {showLabels && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.84rem',
                      fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                      color: location.pathname === item.path ? '#3B82F6' : '#D1D5DB',
                      whiteSpace: 'nowrap',
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  // Mobile: temporary overlay drawer, opens/closes via hamburger
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        anchor="left"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#1F2937',
            borderRight: '1px solid #374151',
            mt: '56px',
          },
        }}
        ModalProps={{ keepMounted: true }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Desktop: permanent drawer, collapsed/expanded via hamburger
  return (
    <Drawer
      variant="permanent"
      anchor="left"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: '#1F2937',
          borderRight: '1px solid #374151',
          mt: '64px',
          overflowX: 'hidden',
          transition: 'width 0.2s ease',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
